import { useEffect } from 'react';
import { useGetMerchantDetails } from '@/modules/stores/hooks/http/use-get-merchant-details';
import { useAnalyticsStoreSlug } from '../hooks/use-analytics-store-slug';

// Função para verificar CSP
function checkCSPForGTM(): void {
  console.log('[GTM CSP Check] 🔍 Verificando Content Security Policy...');
  
  // Verificar meta tag CSP
  const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  if (cspMeta) {
    console.log('[GTM CSP Check] 📋 CSP via meta tag:', cspMeta.getAttribute('content'));
  } else {
    console.log('[GTM CSP Check] ℹ️ Nenhuma meta tag CSP encontrada (pode estar nos headers HTTP)');
  }
  
  // Verificar se domínios do GTM estão acessíveis
  console.log('[GTM CSP Check] 🌐 Domínios necessários para GTM:');
  console.log('  - https://www.googletagmanager.com (script-src, connect-src)');
  console.log('  - https://www.google-analytics.com (script-src, connect-src)');
  console.log('  - https://analytics.google.com (connect-src)');
  console.log('  - https://region1.google-analytics.com (connect-src)');
}

// Função para verificar status do GTM
function verifyGTMStatus(containerId: string): void {
  console.log('[GTM Verify] 🔍 Verificando status do GTM após 3 segundos...');
  
  setTimeout(() => {
    const scriptElement = document.querySelector(
      `script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`
    );
    
    console.log('[GTM Verify] 📊 Status:', {
      scriptInDOM: !!scriptElement,
      dataLayerExists: !!window.dataLayer,
      dataLayerLength: window.dataLayer?.length || 0,
      dataLayerContent: window.dataLayer?.slice(0, 5), // Primeiros 5 itens
    });
    
    // Verificar se o script foi bloqueado
    if (scriptElement) {
      const scriptSrc = scriptElement.getAttribute('src');
      console.log('[GTM Verify] 📝 Script src:', scriptSrc);
      
      // Tentar fazer uma requisição de teste
      fetch(`https://www.googletagmanager.com/gtm.js?id=${containerId}`, { 
        method: 'HEAD',
        mode: 'no-cors' 
      })
        .then(() => {
          console.log('[GTM Verify] ✅ Requisição ao GTM bem-sucedida (no-cors)');
        })
        .catch((error) => {
          console.error('[GTM Verify] ❌ Erro na requisição ao GTM:', error);
          console.error('[GTM Verify] 🚨 Possível bloqueio por CSP ou rede');
        });
    } else {
      console.error('[GTM Verify] ❌ Script do GTM não encontrado no DOM!');
      console.error('[GTM Verify] 🚨 Verifique se o CSP permite googletagmanager.com');
    }
  }, 3000);
}

export function GTMInjector() {
  const slug = useAnalyticsStoreSlug();
  const { data: merchant } = useGetMerchantDetails({ slug });

  useEffect(() => {
    console.log('[GTM Injector] 🎬 Iniciando...', {
      slug,
      hasAnalytics: !!merchant?.analytics,
      gtmId: merchant?.analytics?.google_tag_manager_id,
    });
    
    // Verificar CSP
    checkCSPForGTM();
    
    if (!merchant?.analytics?.google_tag_manager_id) {
      console.log('[GTM Injector] ⚠️ Container ID não encontrado');
      return;
    }

    const containerId = merchant.analytics.google_tag_manager_id;
    console.log('[GTM Injector] 🏷️ Injetando GTM com Container ID:', containerId);

    // Verificar se já foi injetado
    const existingScript = document.querySelector(
      `script[src*="googletagmanager.com/gtm.js?id=${containerId}"]`
    );
    const existingNoscript = document.querySelector(`noscript[data-gtm-id="${containerId}"]`);

    if (existingScript && existingNoscript) {
      console.log('[GTM Injector] ✅ GTM já foi injetado anteriormente');
      verifyGTMStatus(containerId);
      return;
    }

    // Inicializar dataLayer ANTES de injetar o script
    if (!window.dataLayer) {
      window.dataLayer = [];
      console.log('[GTM Injector] 📦 DataLayer inicializado');
    }

    // Enviar evento de inicialização ANTES do script carregar
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    console.log('[GTM Injector] 📤 Evento gtm.js enviado ao dataLayer');

    // Injetar script do GTM no head
    if (!existingScript) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;

      script.onload = () => {
        console.log('[GTM Injector] ✅ Script do GTM carregado com sucesso!');
        console.log('[GTM Injector] 📊 DataLayer após load:', {
          length: window.dataLayer?.length,
          items: window.dataLayer?.slice(0, 5),
        });
      };

      script.onerror = (error) => {
        console.error('[GTM Injector] ❌ ERRO ao carregar script do GTM!', error);
        console.error('[GTM Injector] 🚨 Possíveis causas:');
        console.error('  1. Content Security Policy (CSP) bloqueando googletagmanager.com');
        console.error('  2. Bloqueador de anúncios ativo');
        console.error('  3. Problema de rede');
        console.error('  4. Container ID inválido:', containerId);
        
        // Log adicional para debug
        console.error('[GTM Injector] 🔍 Debug info:', {
          containerId,
          scriptSrc: script.src,
          documentReadyState: document.readyState,
        });
      };

      document.head.appendChild(script);
      console.log('[GTM Injector] 📝 Script do GTM adicionado ao head:', script.src);
    }

    // Injetar noscript do GTM no body
    if (!existingNoscript) {
      const noscript = document.createElement('noscript');
      noscript.setAttribute('data-gtm-id', containerId);

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';

      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
      console.log('[GTM Injector] 📝 Noscript do GTM adicionado ao body');
    }

    console.log('[GTM Injector] 🚀 GTM configurado - aguardando carregamento...');
    
    // Verificar status após alguns segundos
    verifyGTMStatus(containerId);

  }, [merchant?.analytics?.google_tag_manager_id, slug]);

  return null; // Este componente não renderiza nada
}
